import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFilters } from '@panwds/react-filters';
import GlobalProductStatusPageContainer from './components/GlobalProductHeader';
import ProductStatus from './ProductStatus';
import MapContainer from './map/MapContainer';
import { useFetchRegions } from '../../services/api/regions';
import { useFetchMetadata } from '../../services/api/metadata';
import { useFetchProducts } from '../../services/api/products';
import { useFetchIncidents } from '../../services/api/incidents';
import { buildStatusesByProductCity, buildIncidentsByProductCity } from './map/mapStatusHelpers';
import { STATUS_TYPE_ALL_SELECTED } from './map/MapStatusBar';

const FILTER_CONTEXT_ID = 'StatusPageFilterBar';

const StatusPageContainer = ({ onHelpClick }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const viewMode = searchParams.get('view') === 'historic' ? 'historic' : 'realtime';

    const setViewMode = (mode) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('view', mode);
            return next;
        }, { replace: true });
    };

    const { data: products = [], allRegions: productRegions, isLoading: isLoadingProducts } = useFetchProducts();

    const { data: metadata } = useFetchMetadata();
    const mapBoxToken = metadata?.mapboxtoken;
    const googleMapsKey = metadata?.googlemapskey;
    const [planeType, setPlaneType] = useState('all');
    const [statusType, setStatusType] = useState(STATUS_TYPE_ALL_SELECTED);
    const [customHistoricRange, setCustomHistoricRange] = useState(null);

    const { data: regions, isFetching: isFetchingRegions } = useFetchRegions();

    const { filters: pageFilters, setFilters: setPageFilters, initialized: isFilterBarInitialized } = useFilters(
        FILTER_CONTEXT_ID
    );

    const [mapIncidentQuery] = useState(() => 'status=investigating');

    const {
        data: mapIncidents,
        dataUpdatedAt: mapIncidentsUpdatedTime,
        refetch: refetchMapIncidents,
        isLoading: isLoadingMapIncidents,
        isFetching: isFetchingMapIncidents,
    } = useFetchIncidents({
        query: mapIncidentQuery,
        isEnabled: !!isFilterBarInitialized,
        gcTime: 1 * 60 * 1000,
        refetchInterval: 5 * 60 * 1000,
    });

    const statusesByProductCity = useMemo(
        () => buildStatusesByProductCity(mapIncidents),
        [mapIncidents],
    );

    const incidentsByProductCity = useMemo(
        () => buildIncidentsByProductCity(mapIncidents),
        [mapIncidents],
    );

    useEffect(() => {
        if (!isFilterBarInitialized) {
            return;
        }

        setPlaneType(pageFilters?.planeType || 'all');
        setStatusType(pageFilters?.statusType || STATUS_TYPE_ALL_SELECTED);
    }, [pageFilters, setPlaneType, isFilterBarInitialized]);

    return (
        <>
            <GlobalProductStatusPageContainer
                regions={regions}
                isFetchingRegions={isFetchingRegions}
                products={products}
                planeType={planeType}
                statusType={statusType}
                statusesByProductCity={statusesByProductCity}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                onCustomHistoricRangeChange={setCustomHistoricRange}
            />

            <div style={{ display: viewMode === 'realtime' ? undefined : 'none' }}>
                <MapContainer
                    mapBoxToken={mapBoxToken}
                    googleMapsKey={googleMapsKey}
                    regions={productRegions?.length > 0 ? productRegions : regions}
                    planeType={planeType}
                    statusType={statusType}
                    mapIncidents={mapIncidents}
                    mapIncidentsUpdatedTime={mapIncidentsUpdatedTime}
                    refetchMapIncidents={refetchMapIncidents}
                    isFetchingMapIncidents={isFetchingMapIncidents}
                    statusesByProductCity={statusesByProductCity}
                    incidentsByProductCity={incidentsByProductCity}
                />
            </div>

            <div style={{ display: viewMode === 'historic' ? undefined : 'none' }}>
                <ProductStatus
                    products={products}
                    isLoadingProducts={isLoadingProducts}
                    isHistoricView={viewMode === 'historic'}
                    customHistoricRange={customHistoricRange}
                    onHelpClick={onHelpClick}
                />
            </div>
        </>
    );
};

export default React.memo(StatusPageContainer);
